import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useProfile } from './ProfileContext';
import { supabase } from '../lib/supabase';

const AudioSettingsContext = createContext({});

export const useAudioSettings = () => useContext(AudioSettingsContext);

const DEFAULTS = {
  musicEnabled: true,
  soundEnabled: true,
  mainVolume: 0.8,
};

// Map DB column names to context keys
const DB_TO_KEY = {
  pref_music_enabled: 'musicEnabled',
  pref_sound_enabled: 'soundEnabled',
  pref_main_volume: 'mainVolume',
};

const KEY_TO_DB = Object.fromEntries(
  Object.entries(DB_TO_KEY).map(([db, key]) => [key, db])
);

export const AudioSettingsProvider = ({ children }) => {
  const { user } = useAuth();
  const { userProfile } = useProfile();
  const [settings, setSettings] = useState({ ...DEFAULTS });
  const [loaded, setLoaded] = useState(false);
  const saveTimerRef = useRef(null);
  const pendingSaveRef = useRef({});

  // Load preferences from Supabase when user logs in
  useEffect(() => {
    if (!userProfile?.id) {
      setSettings({ ...DEFAULTS });
      setLoaded(false);
      return;
    }

    const loadSettings = async () => {
      const columns = Object.keys(DB_TO_KEY).join(',');
      const { data, error } = await supabase
        .from('users')
        .select(columns)
        .eq('id', userProfile.id)
        .single();

      if (error || !data) {
        setSettings({ ...DEFAULTS });
        localStorage.setItem('musicEnabled', DEFAULTS.musicEnabled.toString());
        localStorage.setItem('soundEnabled', DEFAULTS.soundEnabled.toString());
        setLoaded(true);
        return;
      }

      const loaded = { ...DEFAULTS };
      for (const [dbCol, key] of Object.entries(DB_TO_KEY)) {
        if (data[dbCol] !== null && data[dbCol] !== undefined) {
          loaded[key] = data[dbCol];
        }
      }

      setSettings(loaded);
      localStorage.setItem('musicEnabled', loaded.musicEnabled.toString());
      localStorage.setItem('soundEnabled', loaded.soundEnabled.toString());
      setLoaded(true);
    };

    loadSettings();
  }, [userProfile?.id]);

  // Debounced save to Supabase
  const flushSave = useCallback(async () => {
    if (!userProfile?.id) return;
    const updates = { ...pendingSaveRef.current };
    pendingSaveRef.current = {};

    if (Object.keys(updates).length === 0) return;

    // Convert keys to DB column names
    const dbUpdates = {};
    for (const [key, value] of Object.entries(updates)) {
      const dbCol = KEY_TO_DB[key];
      if (dbCol) {
        dbUpdates[dbCol] = value;
      }
    }

    await supabase
      .from('users')
      .update(dbUpdates)
      .eq('id', userProfile.id);
  }, [userProfile?.id]);

  const updateSetting = useCallback((key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));

    // Sync music/sound to localStorage for BackgroundMusic compatibility
    if (key === 'musicEnabled') {
      localStorage.setItem('musicEnabled', value.toString());
    }
    if (key === 'soundEnabled') {
      localStorage.setItem('soundEnabled', value.toString());
    }

    // Queue for debounced save
    pendingSaveRef.current[key] = value;

    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(flushSave, 500);
  }, [flushSave]);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    setSettings({ ...DEFAULTS });
    localStorage.setItem('musicEnabled', DEFAULTS.musicEnabled.toString());
    localStorage.setItem('soundEnabled', DEFAULTS.soundEnabled.toString());

    // Save all defaults to DB
    pendingSaveRef.current = { ...DEFAULTS };
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(flushSave, 500);
  }, [flushSave]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        flushSave();
      }
    };
  }, [flushSave]);

  // Reset on logout
  useEffect(() => {
    if (!user) {
      setSettings({ ...DEFAULTS });
      setLoaded(false);
      pendingSaveRef.current = {};
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    }
  }, [user]);

  const value = {
    settings,
    loaded,
    updateSetting,
    resetToDefaults,
    DEFAULTS,
  };

  return (
    <AudioSettingsContext.Provider value={value}>
      {children}
    </AudioSettingsContext.Provider>
  );
};
